/** Abre o fluxo SSE de uma pergunta e distribui os eventos para o reducer.
 *
 *  É `fetch` com `ReadableStream`, e não axios nem `EventSource`: o axios não lê
 *  fluxo, e `EventSource` não manda o cabeçalho de autorização. O Bearer vem do
 *  `tokenStore`, renovado antes de abrir quando está perto de vencer, porque a
 *  conexão não passa pelo interceptador de refresh. `AbortController` no Parar e
 *  ao desmontar; erro de rede vira `erro` no estado, com Tentar de novo. */
import { useCallback, useEffect, useMemo, useReducer, useRef } from 'react'
import { CODIGO_LIMITE_DIARIO, assistenteApi, type EnviarMensagemCorpo, type ErroDoServidor } from './api'
import { ESTADO_INICIAL, fluxoReducer, interpretarEvento, type ErroDoFluxo } from './fluxoReducer'
import { criarParserSse } from './sse'
import { tokenParaOFluxo } from './token'

async function lerErroDaResposta(resposta: Response): Promise<ErroDoFluxo> {
  let corpo: ErroDoServidor = {}
  try {
    corpo = (await resposta.json()) as ErroDoServidor
  } catch {
    /* corpo vazio ou não JSON: fica o status */
  }
  const code = corpo.code ?? (resposta.status === 429 ? CODIGO_LIMITE_DIARIO : `HTTP_${resposta.status}`)
  return { code, mensagem: corpo.error ?? '', voltaEm: corpo.volta_em }
}

export function useFluxo() {
  const [estado, despachar] = useReducer(fluxoReducer, ESTADO_INICIAL)
  const controleRef = useRef<AbortController | null>(null)

  const iniciar = useCallback(async (conversaUuid: string, corpo: EnviarMensagemCorpo) => {
    controleRef.current?.abort()
    const controle = new AbortController()
    controleRef.current = controle
    despachar({ tipo: 'abrir' })

    try {
      const token = await tokenParaOFluxo()
      const cabecalhos: Record<string, string> = {
        'Content-Type': 'application/json',
        Accept: 'text/event-stream',
      }
      if (token) cabecalhos.Authorization = `Bearer ${token}`

      const resposta = await fetch(assistenteApi.urlDeMensagens(conversaUuid), {
        method: 'POST',
        headers: cabecalhos,
        body: JSON.stringify(corpo),
        signal: controle.signal,
      })

      if (!resposta.ok) {
        despachar({ tipo: 'falha', erro: await lerErroDaResposta(resposta) })
        return
      }
      if (!resposta.body) {
        despachar({ tipo: 'falha', erro: { code: 'NETWORK_ERROR', mensagem: '' } })
        return
      }

      const leitor = resposta.body.getReader()
      const decodificador = new TextDecoder()
      const parser = criarParserSse()
      for (;;) {
        const { done, value } = await leitor.read()
        const blocos = done
          ? parser.finalizar()
          : parser.alimentar(decodificador.decode(value, { stream: true }))
        for (const bloco of blocos) {
          const evento = interpretarEvento(bloco)
          if (evento) despachar({ tipo: 'evento', evento })
        }
        if (done) break
      }
      despachar({ tipo: 'encerrado' })
    } catch {
      // Parar já despachou o estado; um abort não é falha de rede.
      if (controle.signal.aborted) return
      despachar({ tipo: 'falha', erro: { code: 'NETWORK_ERROR', mensagem: '' } })
    } finally {
      if (controleRef.current === controle) controleRef.current = null
    }
  }, [])

  /** Marca o começo antes de qualquer ida à rede, para o sinal de trabalho
   *  aparecer no instante do envio, mesmo quando a conversa ainda precisa ser
   *  criada. `iniciar` reabre em seguida sem efeito visível. */
  const preparar = useCallback(() => {
    despachar({ tipo: 'abrir' })
  }, [])

  const parar = useCallback(() => {
    controleRef.current?.abort()
    controleRef.current = null
    despachar({ tipo: 'parar' })
  }, [])

  const limpar = useCallback(() => {
    controleRef.current?.abort()
    controleRef.current = null
    despachar({ tipo: 'limpar' })
  }, [])

  /** Falha que acontece antes de o fluxo abrir, como não conseguir criar a
   *  conversa: entra no mesmo estado de erro, com o mesmo Tentar de novo. */
  const falhar = useCallback((erro: ErroDoFluxo) => {
    despachar({ tipo: 'falha', erro })
  }, [])

  useEffect(() => () => controleRef.current?.abort(), [])

  return useMemo(() => ({ estado, iniciar, preparar, parar, limpar, falhar }), [estado, iniciar, preparar, parar, limpar, falhar])
}
